import { getStoryBySlug, StoryData } from '@cinch-nx/data-storyblok'
import { tagError, traceFunction } from '@core/datadog-server'
import {
  GetStaticProps,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from 'next'
import { ServicingPageContent } from './type/servicing-page'
import { servicingFallback } from './data/servicing-fallback'
import { LandingPage } from '@cinch-nx/landing-ui-shared'

const getStory = async (path: string[], preview: boolean | undefined) => {
  const fullSlug = path.join('/')

  return getStoryBySlug<LandingPage>(`servicing/${fullSlug}`, {
    version: preview ? 'draft' : 'published',
  })
}

const storyFallbackMap: Record<string, StoryData<ServicingPageContent>> = {
  servicing: servicingFallback,
}

const getStaticPropsFn: GetStaticProps = async ({
  params,
  preview = false,
}: GetStaticPropsContext) => {
  try {
    const path = (params?.path as string[]) ?? []
    const story = await getStory(path, preview)
    if (!story) return { notFound: true }

    return {
      props: {
        story,
        preview,
        layoutProps: {
          heroProps: story.content.hero[0],
          showBorderRadius: false,
        },
      },
      revalidate: 60,
    }
  } catch (error) {
    if (error instanceof Error || typeof error === 'string') {
      tagError(error)
    }

    const path = (params?.path as string[]) ?? []
    const fallbackKey =
      (Array.isArray(path)
        ? path.join('/')
        : (path as string | undefined) ?? 'servicing') || 'servicing'

    const fallbackStory = storyFallbackMap[fallbackKey]
    if (!fallbackStory) {
      return { notFound: true }
    }

    const serializableStory = JSON.parse(
      JSON.stringify(fallbackStory),
    ) as StoryData<ServicingPageContent>

    return {
      props: {
        story: serializableStory,
        layoutProps: {
          heroProps: serializableStory.content.hero[0],
          showBorderRadius: false,
        },
      },
    }
  }
}

export const getStaticProps = traceFunction(
  'servicing.getStaticProps',
  getStaticPropsFn,
)

export type ServicingPageProps = InferGetStaticPropsType<
  typeof getStaticPropsFn
>
